// v6 — post-tagout replacement request prompt (step 4, team members only)
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import {
  sendNotification, getAdminIds, getAdminEmails,
  getBranchManagerEmails, getBranchManagerIds,
  sendEmail, emailToolTaggedOut, emailBranchSpareRequest,
} from '../lib/notify';
import { uploadPhoto } from '../lib/storage';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';

const REASONS = [
  { label: 'Damage', icon: '💥', color: '#C62828', bg: '#FFEBEE', border: '#FFCDD2' },
  { label: 'Malfunction', icon: '⚙️', color: '#E65100', bg: '#FFF3E0', border: '#FFE0B2' },
  { label: 'Scheduled maintenance', icon: '🔧', color: '#1565C0', bg: '#E3F2FD', border: '#BBDEFB' },
  { label: 'Failed inspection', icon: '❌', color: '#6A1B9A', bg: '#F3E5F5', border: '#E1BEE7' },
  { label: 'Missing parts', icon: '🔍', color: '#BF360C', bg: '#FBE9E7', border: '#FFCCBC' },
  { label: 'Safety concern', icon: '⚠️', color: '#F57F17', bg: '#FFFDE7', border: '#FFF9C4' },
  { label: 'Other', icon: '📝', color: '#37474F', bg: '#ECEFF1', border: '#CFD8DC' },
];

async function logHistory(toolId, eventType, oldStatus, newStatus, changedByName, changedById, notes) {
  await supabase.from('tool_history').insert({
    tool_id: toolId, event_type: eventType,
    old_status: oldStatus || null, new_status: newStatus || null,
    changed_by_name: changedByName || null, changed_by_id: changedById || null,
    notes: notes || null,
  });
}

export default function MobileTagout({ tool, onClose, onSuccess }) {
  const { profile, isTeamMember } = useAuth();
  const [step, setStep] = useState(1); // 1=reason, 2=description, 3=confirm, 4=replacement prompt
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Step 4 state
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestDone, setRequestDone] = useState(false);

  const submitMsg = useMessage();
  const requestMsg = useMessage();

  const selectedReason = REASONS.find(r => r.label === reason);
  const descRequired = reason === 'Other';
  const canSubmit = reason && (description.trim() || !descRequired);

  async function handlePhotoSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    submitMsg.clearMessage();
    try {
      const toolId = parseInt(tool.id);
      const oldStatus = tool.status;

      let photoPath = null;
      if (photo) {
        setPhotoUploading(true);
        const { path, error: uploadError } = await uploadPhoto(photo, 'tagouts');
        setPhotoUploading(false);
        if (uploadError) throw new Error('Photo upload failed: ' + uploadError);
        photoPath = path;
      }

      const { error: toolError } = await supabase
        .from('tools')
        .update({ status: 'tagged_out' })
        .eq('id', toolId);
      if (toolError) throw toolError;

      const { error: logError } = await supabase.from('tagout_log').insert({
        tool_id: toolId, reason, issue_description: description || reason,
        tagged_by_name: profile?.full_name,
        status: 'tagged_out',
        shop_name: null,
        estimated_repair_cost: null,
        photo_url: photoPath,
      });
      if (logError) throw logError;

      await logHistory(
        toolId, 'tagged_out', oldStatus, 'tagged_out',
        profile?.full_name, profile?.id,
        `${reason}${description ? ': ' + description : ''}`
      );

      const adminIds = await getAdminIds();
      await sendNotification(
            adminIds,
            `Tool tagged out: ${tool.name}`,
            `${profile?.full_name} tagged out ${tool.name}. Reason: ${reason}.`,
            'tool_tagged_out',
            'tools',
            'tagout',
            tool.id
          );

      const adminEmails = await getAdminEmails();
      const managerEmails = await getBranchManagerEmails(tool.branch_id);
      const allEmails = [...new Set([...adminEmails, ...managerEmails])];
      await sendEmail(
        allEmails,
        `Tool tagged out: ${tool.name}`,
        emailToolTaggedOut(
          tool.name,
          tool.branches?.name || 'Unknown branch',
          reason,
          profile?.full_name,
          tool.station_truck,
          tool.serial_number,
          // #126 / M3, 78v - opens this tag-out rather than the Fleet home.
          tool.id,
        )
      );

      setSaving(false);

      // Only show replacement prompt for team members
      if (isTeamMember) {
        setStep(4);
      } else {
        onSuccess?.();
      }
    } catch (err) {
      submitMsg.showError('Something went wrong: ' + err.message);
      setSaving(false);
      setPhotoUploading(false);
    }
  }

  async function requestReplacement() {
    setRequestSaving(true);
    requestMsg.clearMessage();
    try {
      const branchManagerIds = await getBranchManagerIds(tool.branch_id);
      const branchManagerEmails = await getBranchManagerEmails(tool.branch_id);

      await supabase.from('spare_requests').insert({
        tool_name: tool.name,
        model_number: tool.model_number || null,
        serial_number: tool.serial_number || null,
        quantity: 1,
        manager_name: profile?.full_name,
        requested_by: profile?.id,
        requesting_branch_id: tool.branch_id,
        requesting_branch_name: tool.branches?.name || '',
        notes: `Replacement requested after tag-out by ${profile?.full_name}. Reason: ${reason}.`,
        status: 'pending',
        submitted_at: new Date().toISOString(),
      });

      await sendNotification(
            branchManagerIds,
            `Replacement requested: ${tool.name}`,
            `${profile?.full_name} tagged out ${tool.name} at Station ${tool.station_truck || '—'} and is requesting a replacement spare.`,
            'spare_request',
            'tools',
            'requests'
          );

      await sendEmail(
        branchManagerEmails,
        `Branch spare requested: ${tool.name}`,
        emailBranchSpareRequest(
          tool.name,
          tool.branches?.name || 'Unknown branch',
          tool.department || '—',
          tool.station_truck || '—',
          profile?.full_name,
        )
      );

      setRequestDone(true);
    } catch (err) {
      requestMsg.showError('Something went wrong: ' + err.message);
    } finally {
      setRequestSaving(false);
    }
  }

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'white', zIndex: 2000,
      display: 'flex', flexDirection: 'column', overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        background: step === 4 ? '#2E7D32' : '#C62828',
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
        transition: 'background 0.3s',
      }}>
        {step < 4 && (
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
            color: 'white', padding: '6px 10px', fontSize: 18, cursor: 'pointer',
          }}>←</button>
        )}
        <div>
          <div style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>
            {step === 4 ? 'Tool tagged out' : 'Tag out tool'}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{tool.name}</div>
        </div>
        {step < 4 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: step >= s ? 'white' : 'rgba(255,255,255,0.3)',
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Tool info strip — hidden on step 4 */}
      {step < 4 && (
        <div style={{
          background: '#FFF8E8', borderBottom: '1px solid #FFE0A0',
          padding: '12px 20px', fontSize: 13, color: '#5C5C5C',
          display: 'flex', gap: 16, flexWrap: 'wrap',
        }}>
          <span>Tagged by: <strong style={{ color: '#0F0F0F' }}>{profile?.full_name}</strong></span>
          {tool.station_truck && <span>Station: <strong style={{ color: '#0F0F0F' }}>{tool.station_truck}</strong></span>}
          {tool.serial_number && <span>S/N: <strong style={{ color: '#0F0F0F' }}>{tool.serial_number}</strong></span>}
        </div>
      )}

      <div style={{ flex: 1, padding: '24px 20px', maxWidth: 500, margin: '0 auto', width: '100%' }}>

        {/* ── STEP 1: REASON ── */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#0F0F0F' }}>
              What's the reason for tagging out?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {REASONS.map(r => (
                <button
                  key={r.label}
                  onClick={() => { setReason(r.label); setStep(2); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 18px', borderRadius: 12, cursor: 'pointer',
                    border: `2px solid ${reason === r.label ? r.color : r.border}`,
                    background: reason === r.label ? r.bg : 'white',
                    textAlign: 'left', transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{r.icon}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: reason === r.label ? r.color : '#0F0F0F' }}>{r.label}</span>
                  <span style={{ marginLeft: 'auto', color: '#D0D0D0', fontSize: 18 }}>→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 2: DESCRIPTION ── */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#0F0F0F' }}>Describe the issue</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 20, marginBottom: 20,
              background: selectedReason?.bg, color: selectedReason?.color,
              border: `1px solid ${selectedReason?.border}`,
              fontSize: 13, fontWeight: 600,
            }}>
              {selectedReason?.icon} {reason}
            </div>
            <textarea
              autoFocus
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={reason === 'Other' ? 'Required — describe the issue...' : 'Optional — describe what you observed...'}
              style={{
                width: '100%', minHeight: 140, padding: 16,
                fontSize: 16, border: '2px solid #D0D0D0', borderRadius: 12,
                fontFamily: 'Barlow, sans-serif', resize: 'vertical', lineHeight: 1.5,
              }}
              onFocus={e => e.target.style.borderColor = '#C62828'}
              onBlur={e => e.target.style.borderColor = '#D0D0D0'}
            />
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#5C5C5C', marginBottom: 8 }}>
                📷 Add photo <span style={{ fontWeight: 400, color: '#9A9A9A' }}>(optional)</span>
              </div>
              {photoPreview ? (
                <div style={{ position: 'relative' }}>
                  <img src={photoPreview} alt="Preview" style={{ width: '100%', borderRadius: 10, maxHeight: 200, objectFit: 'cover' }} />
                  <button onClick={() => { setPhoto(null); setPhotoPreview(null); }} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', color: 'white', width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              ) : (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, border: '2px dashed #D0D0D0', cursor: 'pointer', background: '#F8F8F8' }}>
                  <span style={{ fontSize: 22 }}>📷</span>
                  <span style={{ fontSize: 14, color: '#5C5C5C' }}>Tap to take or upload a photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            {reason === 'Other' && !description.trim() && (
              <div style={{ color: '#C62828', fontSize: 12, marginTop: 6 }}>Description is required when selecting "Other"</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700, border: '2px solid #E0E0E0', background: 'white', color: '#5C5C5C', cursor: 'pointer' }}>← Back</button>
              <button
                onClick={() => { if (descRequired && !description.trim()) return; setStep(3); }}
                disabled={descRequired && !description.trim()}
                style={{
                  flex: 2, padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700, border: 'none',
                  background: (descRequired && !description.trim()) ? '#E0E0E0' : '#C62828',
                  color: (descRequired && !description.trim()) ? '#9A9A9A' : 'white',
                  cursor: (descRequired && !description.trim()) ? 'not-allowed' : 'pointer',
                }}
              >Review →</button>
            </div>
          </div>
        )}

        {/* ── STEP 3: CONFIRM ── */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#0F0F0F' }}>Confirm tag-out</div>
            <div style={{ background: '#F8F8F8', borderRadius: 12, padding: 20, border: '1px solid #E0E0E0', marginBottom: 24 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tool</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{tool.name}</div>
                {tool.serial_number && <div style={{ fontSize: 13, color: '#5C5C5C' }}>S/N: {tool.serial_number}</div>}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Reason</div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 16,
                  background: selectedReason?.bg, color: selectedReason?.color,
                  border: `1px solid ${selectedReason?.border}`,
                  fontSize: 13, fontWeight: 600,
                }}>
                  {selectedReason?.icon} {reason}
                </div>
              </div>
              {description && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Description</div>
                  <div style={{ fontSize: 14, color: '#0F0F0F', fontStyle: 'italic' }}>"{description}"</div>
                </div>
              )}
              {photoPreview && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Photo</div>
                  <img src={photoPreview} alt="Damage" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
                </div>
              )}
            </div>

            <div style={{ background: '#FFF3E0', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#9A5F00', marginBottom: 16, border: '1px solid #FFE0B2' }}>
              ⚠️ This will mark the tool as <strong>Tagged out</strong> and notify your manager.
            </div>

            <InlineMessage type={submitMsg.type} message={submitMsg.message} onDismiss={submitMsg.clearMessage} style={{ marginBottom: 16 }} />

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700, border: '2px solid #E0E0E0', background: 'white', color: '#5C5C5C', cursor: 'pointer' }}>← Back</button>
              <button
                onClick={submit}
                disabled={saving}
                style={{
                  flex: 2, padding: '16px', borderRadius: 12, fontSize: 16, fontWeight: 700, border: 'none',
                  background: saving ? '#E0E0E0' : '#C62828',
                  color: saving ? '#9A9A9A' : 'white',
                  cursor: saving ? 'not-allowed' : 'pointer', letterSpacing: 0.5,
                }}
              >{photoUploading ? 'Uploading photo...' : saving ? 'Submitting...' : '⚠️ Tag out tool'}</button>
            </div>
          </div>
        )}

        {/* ── STEP 4: REPLACEMENT PROMPT (team members only) ── */}
        {step === 4 && (
          <div>
            {/* Success confirmation */}
            <div style={{
              background: '#E8F5E9', borderRadius: 12, padding: 20,
              border: '1px solid #C8E6C9', marginBottom: 28, textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Tag-out recorded</div>
              <div style={{ fontSize: 13, color: '#5C5C5C' }}>
                {tool.name} has been tagged out and your manager has been notified.
              </div>
            </div>

            {!requestDone ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 8 }}>
                  Do you need a replacement?
                </div>
                <div style={{ fontSize: 13, color: '#5C5C5C', marginBottom: 24, lineHeight: 1.5 }}>
                  Your manager will be notified to assign a spare tool to{tool.station_truck ? ` Station ${tool.station_truck}` : ' your station'}.
                </div>

                {/* Tool + station summary */}
                <div style={{
                  background: '#F8F8F8', borderRadius: 10, padding: '14px 16px',
                  border: '1px solid #E0E0E0', marginBottom: 24,
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#9A9A9A', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>Tool</span>
                    <span style={{ color: '#0F0F0F', fontWeight: 600 }}>{tool.name}</span>
                  </div>
                  {tool.station_truck && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#9A9A9A', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>Station</span>
                      <span style={{ color: '#0F0F0F', fontWeight: 600 }}>{tool.station_truck}</span>
                    </div>
                  )}
                  {tool.department && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#9A9A9A', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>Department</span>
                      <span style={{ color: '#0F0F0F', fontWeight: 600 }}>{tool.department}</span>
                    </div>
                  )}
                </div>

                <InlineMessage type={requestMsg.type} message={requestMsg.message} onDismiss={requestMsg.clearMessage} style={{ marginBottom: 16 }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    onClick={requestReplacement}
                    disabled={requestSaving}
                    style={{
                      width: '100%', padding: '16px', borderRadius: 12, fontSize: 16,
                      fontWeight: 700, border: 'none',
                      background: requestSaving ? '#E0E0E0' : '#1565C0',
                      color: requestSaving ? '#9A9A9A' : 'white',
                      cursor: requestSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {requestSaving ? 'Sending request...' : '🔄 Yes, request a replacement'}
                  </button>
                  <button
                    onClick={() => onSuccess?.()}
                    disabled={requestSaving}
                    style={{
                      width: '100%', padding: '14px', borderRadius: 12, fontSize: 15,
                      fontWeight: 600, border: '2px solid #E0E0E0', background: 'white',
                      color: '#5C5C5C', cursor: requestSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    No thanks, I'm done
                  </button>
                </div>
              </>
            ) : (
              /* Request sent confirmation */
              <>
                <div style={{
                  background: '#E3F2FD', borderRadius: 12, padding: 20,
                  border: '1px solid #BBDEFB', marginBottom: 28, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1565C0', marginBottom: 4 }}>Request sent</div>
                  <div style={{ fontSize: 13, color: '#5C5C5C', lineHeight: 1.5 }}>
                    Your manager has been notified and will assign a spare to your station.
                  </div>
                </div>
                <button
                  onClick={() => onSuccess?.()}
                  style={{
                    width: '100%', padding: '16px', borderRadius: 12, fontSize: 16,
                    fontWeight: 700, border: 'none', background: '#2E7D32',
                    color: 'white', cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}